import { useMutation, useQueryClient, type MutationFunction } from '@tanstack/react-query'
import { getConfigOptions } from './getConfig'
import { authClient } from '@/shared/utils/axios/api'
import type { AuthResponse, LoginCallbackInput, OidcClientConfig } from '../types/authTypes'
import type { MutationConfig } from '@/shared/utils/reactQuery'
import { config as appConfig } from '@/config'

type LoginCallbackMutationFunctionProps = {
  code: string
}

type LoginCallbackProps = LoginCallbackMutationFunctionProps & {
  config: OidcClientConfig
}

const loginCallback = async ({ config, code }: LoginCallbackProps): Promise<AuthResponse> => {
  const body: LoginCallbackInput = {
    grant_type: 'authorization_code',
    code: code,
    client_id: appConfig.auth.clientId,
    redirect_uri: appConfig.auth.loginRedirectUri,
  }
  const { data } = await authClient.post<AuthResponse>(new URL(config.token_endpoint).toString(), new URLSearchParams(body))
  return data
}

type UseLoginCallbackProps = {
  mutationConfig?: MutationConfig<MutationFunction<AuthResponse, LoginCallbackMutationFunctionProps>>
}

const useLoginCallback = ({ mutationConfig }: UseLoginCallbackProps = {}) => {
  const { ...restConfig } = mutationConfig || {}
  const queryClient = useQueryClient()

  return useMutation({
    ...restConfig,
    meta: { silent: true },
    mutationFn: async ({ code }: LoginCallbackMutationFunctionProps) => {
      const configQuery = getConfigOptions()
      const config = queryClient.getQueryData(configQuery.queryKey) ?? (await queryClient.fetchQuery(configQuery))
      return loginCallback({ code, config })
    },
  })
}

export { loginCallback, useLoginCallback }
